import React, { Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useWidth } from '../context/WidthContext';
import ParticleBackground from '../components/ParticleBackground';
import { AnimatePresence } from 'framer-motion';
import PageTransition from '../components/PageTransition';

// Lazy load pages for performance optimization (Code Splitting)
const Home = React.lazy(() => import('./Home'));
const Work = React.lazy(() => import('./Work'));
const Skills = React.lazy(() => import('./Skills'));
const Projects = React.lazy(() => import('./Projects'));
const About = React.lazy(() => import('./About'));
const Contact = React.lazy(() => import('./Contact'));

// Fix for AnimatePresence type error (TS2786)
const AnimatePresenceWrapper = AnimatePresence as unknown as React.FC<any>;

const LoadingFallback = () => (
  <Box sx={{
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <CircularProgress size={40} thickness={4} />
  </Box>
);

const MainPage: React.FC = () => {
  const { isFullWidth } = useWidth();
  const location = useLocation();

  return (
    <Box
      sx={{
        height: '100vh',
        minHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ParticleBackground />
      <Navbar />
      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '0px', background: 'transparent' },
          pt: { xs: 12, sm: 14 },
          pb: 0,
          px: isFullWidth
              ? { xs: 2, sm: 3 }
              : { xs: 3, sm: 4, md: 6, lg: 8 },
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Box
          sx={{
            width: isFullWidth ? '100%' : '75%',
            margin: '0 auto',
            transition: 'width 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AnimatePresenceWrapper mode="wait">
            <Suspense fallback={<LoadingFallback />}>
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<PageTransition><Home /></PageTransition>} />
                <Route path="/work" element={<PageTransition><Work /></PageTransition>} />
                <Route path="/skills" element={<PageTransition><Skills /></PageTransition>} />
                <Route path="/projects" element={<PageTransition><Projects /></PageTransition>} />
                <Route path="/about" element={<PageTransition><About /></PageTransition>} />
                <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
              </Routes>
            </Suspense>
          </AnimatePresenceWrapper>
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default MainPage;
